import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, ReplaySubject, filter, firstValueFrom, take } from 'rxjs';
import { AppService, OrderType } from 'briteatlib';
import { MenuData, MenuItemData, MenuItemOptionData, OptionPrice, OrderItemOption } from 'briteatlib';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Promotion } from 'briteatlib';
import { OrderSource } from 'projects/briteatlib/src';

export const unorderableTranslations: Record<string, string> = {
    NotAvailable: 'Not Available',
    MinOrderAmount: 'Minimum order amount not met',
    MinOrderQuantity: 'Minimum order quantity not met',
    NoZoneAvailable: 'Enter a delivery postcode to check availability',
    ExcludedPostcode: 'Sorry, you are too far — we do not deliver to your postcode',
    Expired: 'Expired',
    OutOfOperating: 'Out of operating hours',
    UnavailableType: 'Unavailable in this order type',
    NoCustomer: 'Customer not specified',
    NoLocation: 'Location not specified',
    InvalidType: 'Invalid order type'
}

@Injectable({
  providedIn: 'root'
})
export class MenuService {

  static defaultOrderType?: OrderType;
  menuList$ = new BehaviorSubject<MenuData[]>([]);
  orderType$ = new BehaviorSubject<OrderType>(MenuService.defaultOrderType);
  promotions$ = new ReplaySubject<Promotion[]>(1);
  private orderSource: OrderSource = 'Online';
  setOrderSource(source: OrderSource) {
    this.orderSource = source;
  }

  constructor(private http: HttpClient, appService: AppService) {
    appService.activeBusiness$.pipe(filter(_ => !!_), takeUntilDestroyed()).subscribe(async biz => {
      if (appService.isBrowser) this.loadOrderType(biz);

      const data = await this.getBusinessData();
      const list = data.MenuData;
      this.calcMenuItems(list);
      this.menuList$.next(list?.filter(_ => _.Visible) || []);
      this.promotions$.next(data.Promotions);
    });

    this.orderType$.subscribe(orderType => {
      if (appService.isBrowser)
        this.persistOrderType(orderType);
      const list = this.menuList$.value;
      this.calcMenuItems(list);
      this.menuList$.next(list.filter(_ => _.Visible));
    });
  }

  private calcMenuItems(list: MenuData[]) {
    list?.forEach(menu => {
      menu.Items?.forEach(item => {
        this.calcItem(item);
      });
      menu.Visible = !menu.Hidden && (menu.Items?.length > 0 || menu.Promotions?.length > 0);
    });
  }

  private persistOrderType(orderType: OrderType) {
    localStorage.setItem('MenuService_orderType', orderType);
  }
  private loadOrderType(business: any) {
    let orderType = localStorage.getItem('MenuService_orderType') as OrderType;
    if (!orderType || orderType.toString() == 'undefined')
      orderType = (business.OrderTypes?.find(_ => true) || MenuService.defaultOrderType) as OrderType;
    this.orderType$.next(orderType || MenuService.defaultOrderType);
  }

  private async getBusinessData() {
    return firstValueFrom(this.http.get<{ MenuData: MenuData[], Promotions: Promotion[] }>('/api/menu/menu-data'));
  }

  getPriceFromOption(prices?: OptionPrice[], selectedOptions?: { id: string; quantity: number; }[], orderType?: OrderType): number {
    const price = (
      prices?.find(_ => selectedOptions?.find(x => x.id == _.MenuOptionId)) ||
      prices?.find(_ => _.MenuOptionId == null) ||
      prices?.find(_ => true)  //first price
    );

    if (!price) return 0;

    const orderTypePriceField = orderType && orderType != 'Default' ? `${orderType}Price` as keyof OptionPrice : 'Price';
    return (price[orderTypePriceField] as number | undefined) ?? price.Price ?? 0;
  }

  //calculate item price, is ready to add to order, ...
  calcItem(item: MenuItemData, orderType?: OrderType): void {
    if (!orderType) orderType = this.orderType$.value;

    if (!item._Order) item._Order = {};
    const menu = this.getMenu(item.MenuId);

    const selectedOptions = item.Options?.flatMap(option => option._Order?.SelectedChoices?.filter(_ => _.quantity > 0).sort((a, b) => a.id.localeCompare(b.id))).filter(_ => !!_) || [];
    item.Options?.forEach(option => this.calcChoices(option, selectedOptions, orderType));

    item._Order.Price = (item.PriceBySource?.[this.orderSource] || menu?.PriceBySource?.[this.orderSource] || 0) + (item.BasePriceByType?.[orderType] || 0);
    item._Order.FromPrice = item._Order.Price + (item.HasVariations
      ? item.Options?.find(o => o.IsVariation)?._Order?.FromPrice || 0 : 0);
    item._Order.OrderPrice = item._Order.Price;  

    item._Order.OrderPrice += item.Options?.flatMap(option => option._Order?.OrderPrice || 0)?.reduce((a, b) => a + b, 0) || 0;
    item._Order.ReadyToAdd = !(item.Options?.length > 0) || item.Options?.every(option => option._Order?.ReadyForOrder);
    
    item._Order.Fingerprint = `${item.Id}_${selectedOptions.map(_ => `${_.quantity}x${_.id}`).join('_')}`;
  }

  private calcChoices(option: MenuItemOptionData, selectedOptions: { id: string; quantity: number; }[], orderType?: OrderType): void {

    option.Choices?.forEach(choice => {
      if (!choice._Order) choice._Order = {};
      const sourcePrice = choice.PriceBySource?.[this.orderSource] || 0;
      choice._Order.Price = sourcePrice + this.getPriceFromOption(choice.Prices, selectedOptions, orderType);
    });

    //calculate price based on selected options
    if (!option._Order) option._Order = {};
    option._Order.FromPrice = option.Choices?.map(choice => choice._Order?.Price || 0).sort((a, b) => a - b)[0] || 0;
    option._Order.OrderPrice = option.Choices?.filter(choice => selectedOptions.find(_ => _.id == choice.MenuOptionId))
      .flatMap(choice => new Array(choice._Order.Quantity).fill(choice._Order?.Price || 0))
      .sort((a, b) => a - b)
      .slice(option.FreeSelection || 0) //skip free selections
      .reduce((a, b) => a + b, 0) || 0;

    if (option.MaxSelection > 0) {
      option.Choices?.forEach(choice =>
        choice._Order.Disabled = (option._Order.TotalSelected >= option.MaxSelection) && !selectedOptions.find(_ => _.id == choice.MenuOptionId)
      );
    }

    option._Order.ReadyForOrder = !(option.MinSelection > 0) || option._Order.TotalSelected >= option.MinSelection;
    option._Order.TotalSelected = option.Choices?.reduce((a, b) => a + (b._Order?.Quantity || 0), 0);
  }

  getMenuItem(menuId: string, itemId: string, selectedOptions?: OrderItemOption[]): MenuItemData {
    const menu = this.getMenu(menuId);
    const item = menu?.Items?.find(item => item.Id == itemId);
    item.Options?.forEach(option => {
      option._Order = {};
      option.Choices?.forEach(choice => {
        choice._Order = {};
        const selected = selectedOptions?.find(_ => _.OptionId == choice.MenuOptionId);
        if (selected)
          choice._Order.Quantity = selected.Quantity;
      });
      option._Order.SelectedChoices = option.Choices?.filter(choice => choice._Order?.Quantity > 0).map(choice => ({ id: choice.MenuOptionId, quantity: choice._Order.Quantity }));
      option._Order.TotalSelected = option.Choices?.reduce((a, b) => a + (b._Order?.Quantity || 0), 0) || 0;
    });
    this.calcItem(item);
    return item;
  }

  private getMenu(menuId: string) {
    return this.menuList$.value.find(menu => menu.Id == menuId);
  }
}
